import { Link } from 'react-router-dom'
import {
  LayoutDashboard,
  Bot,
  MessageSquare,
  Play,
  FolderKanban,
  Server,
  Search,
  BookOpen,
  Database,
  TrendingUp,
  Coins,
  DollarSign,
  Users,
  FileText,
  Wallet,
  PanelLeftClose,
  PanelLeft,
  GitBranch,
} from 'lucide-react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { useSidebar } from '@/contexts/SidebarContext'
import { usePermissions } from '@/hooks/usePermissions'
import { SidebarNavSection } from './SidebarNavSection'
import { SidebarNavItem } from './SidebarNavItem'
import { cn } from '@/lib/utils'

/**
 * Fixed dark sidebar navigation for the Ignix67 platform.
 * 
 * Features:
 * - Expanded (240px): icon + text, section headers
 * - Collapsed (64px): icon-only, tooltips, dividers
 * - Persist state in localStorage
 * - Mobile: overlay with backdrop
 */
export function AppSidebar() {
  const { collapsed, toggle, mobileOpen, setMobileOpen } = useSidebar()
  const { hasPermission, role } = usePermissions()
  const canViewConfig = hasPermission('config:view')
  const isSuperAdmin = role === 'super_admin'

  return (
    <TooltipProvider>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          'flex flex-col bg-sidebar border-r border-sidebar-border transition-all duration-200 ease-in-out',
          // Desktop: static in layout
          'hidden lg:flex',
          collapsed ? 'w-16' : 'w-60',
          // Mobile: overlay
          mobileOpen && '!fixed inset-y-0 left-0 z-50 !flex w-60'
        )}
      >
        {/* Logo / Brand */}
        <div className={cn(
          'flex items-center h-12 border-b border-sidebar-border shrink-0',
          collapsed ? 'justify-center px-2' : 'px-4 gap-2.5'
        )}>
          <Link to="/" className="flex items-center gap-2.5 overflow-hidden" onClick={() => setMobileOpen(false)}>
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-sidebar-accent shrink-0">
              <span className="text-sm font-bold text-sidebar-accent-foreground">ix</span>
            </div>
            {!collapsed && (
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold text-sidebar-accent-foreground truncate">Ignix67</span>
                <span className="text-[10px] text-sidebar-foreground/50 truncate">Agentic Platform</span>
              </div>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 px-2">
          {/* Core */}
          <SidebarNavSection>
            <SidebarNavItem label="Dashboard" href="/" icon={LayoutDashboard} />
            <SidebarNavItem label="Assistant" href="/assistant" icon={Bot} />
          </SidebarNavSection>

          {/* Work */}
          <SidebarNavSection label="Work">
            <SidebarNavItem label="Sessions" href="/sessions" icon={MessageSquare} matchPaths={['/sessions', '/orchestrate']} />
            <SidebarNavItem label="Runs" href="/runs" icon={Play} />
            <SidebarNavItem label="Projects" href="/projects" icon={FolderKanban} />
            <SidebarNavItem label="Services" href="/service-catalog" icon={Server} />
          </SidebarNavSection>

          {/* Knowledge */}
          <SidebarNavSection label="Knowledge">
            <SidebarNavItem label="Search" href="/knowledge/search" icon={Search} />
            <SidebarNavItem label="Glossary" href="/knowledge/glossary" icon={BookOpen} />
            <SidebarNavItem label="Sources" href="/knowledge/sources" icon={Database} />
          </SidebarNavSection>

          {/* Analytics */}
          <SidebarNavSection label="Analytics">
            <SidebarNavItem label="Adoption" href="/stats/adoption" icon={TrendingUp} />
            <SidebarNavItem label="Token Usage" href="/stats/tokens" icon={Coins} />
          </SidebarNavSection>

          {/* Admin / Configuration */}
          {canViewConfig && (
            <SidebarNavSection label="Admin">
              <SidebarNavItem label="Model Pricing" href="/configuration/model-pricing" icon={DollarSign} />
              <SidebarNavItem label="Repository access" href="/configuration/repository-access" icon={GitBranch} />
              {isSuperAdmin && (
                <SidebarNavItem label="Users" href="/configuration/users" icon={Users} />
              )}
              {isSuperAdmin && (
                <SidebarNavItem label="Prompts" href="/configuration/prompts" icon={FileText} />
              )}
              {isSuperAdmin && (
                <SidebarNavItem label="Budget" href="/configuration/budget" icon={Wallet} />
              )}
            </SidebarNavSection>
          )}
        </nav>

        {/* Footer — collapse toggle */}
        <div className="border-t border-sidebar-border p-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggle}
            className={cn(
              'w-full text-sidebar-foreground hover:bg-sidebar-muted hover:text-sidebar-accent-foreground',
              collapsed ? 'justify-center px-0' : 'justify-start gap-2'
            )}
          >
            {collapsed ? (
              <PanelLeft className="h-4 w-4" />
            ) : (
              <>
                <PanelLeftClose className="h-4 w-4" />
                <span className="text-xs">Collapse</span>
              </>
            )}
          </Button>
        </div>
      </aside>
    </TooltipProvider>
  )
}
